type PeriodScore = {
  matchStatusCode?: number;
  homeScore?: number | null;
  awayScore?: number | null;
};

export type MatchResult = {
  homeScore?: number | null;
  awayScore?: number | null;
  periodScores?: Array<PeriodScore | null> | null;
};

type PeriodFormat = {
  periodCodes: readonly number[];
  prefix: "S" | "Q" | "P" | "In";
};

const SET_PERIOD_CODES = [8, 9, 10, 11, 12] as const;
const BASKETBALL_PERIOD_CODES = [13, 14, 15, 16] as const;
const ICEHOCKEY_PERIOD_CODES = [1, 2, 3] as const;
const BASEBALL_PERIOD_CODES = [531, 532, 533, 534, 535, 536, 537, 538, 539] as const;

const getPeriodFormat = (sportType: string): PeriodFormat | undefined => {
  if (
    sportType === "VOLLEYBALL" ||
    sportType === "TABLETENNIS" ||
    sportType === "BADMINTON" ||
    sportType === "TENNIS"
  ) {
    return { periodCodes: SET_PERIOD_CODES, prefix: "S" };
  }

  if (sportType === "BASKETBALL") {
    return { periodCodes: BASKETBALL_PERIOD_CODES, prefix: "Q" };
  }

  if (sportType === "ICEHOCKEY") {
    return { periodCodes: ICEHOCKEY_PERIOD_CODES, prefix: "P" };
  }

  if (sportType === "BASEBALL") {
    return { periodCodes: BASEBALL_PERIOD_CODES, prefix: "In" };
  }

  return undefined;
};

const formatPeriodResult = (
  periodScores: Array<PeriodScore | null>,
  periodFormat: PeriodFormat,
) => {
  return periodFormat.periodCodes
    .map((matchStatusCode, index) => {
      const periodScore = periodScores.find(
        (score) => score?.matchStatusCode === matchStatusCode,
      );

      if (
        !periodScore ||
        periodScore.homeScore == null ||
        periodScore.awayScore == null
      ) {
        return "";
      }

      return `${periodFormat.prefix}${index + 1}[${periodScore.homeScore}:${periodScore.awayScore}]`;
    })
    .filter(Boolean)
    .join(" ");
};

const formatFootballResult = (periodScores: Array<PeriodScore | null>) => {
  const firstHalf = periodScores.find((score) => score?.matchStatusCode === 6);
  const secondHalf = periodScores.find((score) => score?.matchStatusCode === 7);
  const penalties = periodScores.find((score) => score?.matchStatusCode === 50);
  const scores: string[] = [];

  if (firstHalf && firstHalf.homeScore != null && firstHalf.awayScore != null) {
    scores.push(`HT [${firstHalf.homeScore}:${firstHalf.awayScore}]`);

    if (secondHalf && secondHalf.homeScore != null && secondHalf.awayScore != null) {
      const homeScore = firstHalf.homeScore + secondHalf.homeScore;
      const awayScore = firstHalf.awayScore + secondHalf.awayScore;
      scores.push(`FT [${homeScore}:${awayScore}]`);
    }
  }

  if (penalties && penalties.homeScore != null && penalties.awayScore != null) {
    scores.push(`PEN [${penalties.homeScore}:${penalties.awayScore}]`);
  }

  return scores.join(" | ");
};

const formatMatchResult = (
  sportType: string,
  result?: MatchResult | null,
) => {
  if (!result) {
    return "";
  }

  if (sportType === "FOOTBALL") {
    return formatFootballResult(result.periodScores ?? []);
  }

  const periodFormat = getPeriodFormat(sportType);
  if (periodFormat) {
    return formatPeriodResult(result.periodScores ?? [], periodFormat);
  }

  return `HT [${result.periodScores?.[0]?.homeScore}:${result.periodScores?.[0]?.awayScore}] | FT [${result.homeScore}:${result.awayScore}]`;
};

export default formatMatchResult;
